import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardBody, Button } from "components/ui";
import { ROUTES } from "constants/index";
import { processPaymentResult } from "services/payment.service";
import { useToast } from "hooks/useToast";

export const PaymentResultPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<"loading" | "success" | "failed" | "error">("loading");
  const [message, setMessage] = useState("Processing payment...");
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple calls
    if (hasProcessed.current) {
      return;
    }

    // Extract params once to avoid re-renders
    const orderId = searchParams.get("orderId");
    const userId = searchParams.get("userId");
    const accountId = searchParams.get("accountId");
    const companyId = searchParams.get("companyId");
    const packageDetailsId = searchParams.get("packageDetailsId");
    
    if (!orderId || !packageDetailsId) {
      hasProcessed.current = true;
      if (!orderId) {
        setStatus("error");
        setMessage("Order ID is missing from the payment result.");
      } else {
        setStatus("error");
        setMessage("Package details ID is missing from the payment result.");
      }
      return;
    }

    hasProcessed.current = true;

    const processPayment = async () => {
      try {
        const resultIndicator = searchParams.get("resultIndicator");
        const sessionVersion = searchParams.get("sessionVersion");
        const checkoutVersion = searchParams.get("checkoutVersion");
  
        const result = await processPaymentResult({
          orderId,
          accountId: accountId ? parseInt(accountId) : undefined,
          companyId: companyId ? parseInt(companyId) : 0,
          packageDetailsId: parseInt(packageDetailsId),
          resultIndicator: resultIndicator || undefined,
          sessionVersion: sessionVersion || undefined,
          checkoutVersion: checkoutVersion || undefined,
        });  
        if (result && result.success === true) {
          setStatus("success");
          setMessage("Payment processed successfully!");
          toast.success("Payment has been processed successfully.");
        } else {
          setStatus("failed");
          setMessage("Payment processing failed. Please contact support.");
          toast.error("Failed to process payment.");
        }
      } catch (error: any) { 
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED' || error?.message?.toLowerCase().includes('aborted') || error?.message?.toLowerCase().includes('canceled')) {
          console.log("Request was aborted/canceled");
          return;
        }
        
        console.error("Error processing payment:", error);
        setStatus("error");
        const errorMessage = error?.response?.data?.message || error?.message || "An error occurred while processing the payment.";
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    processPayment();
  }, [searchParams.toString()]); // Use toString() to get a stable string representation

  const getStatusIcon = () => {
    switch (status) {
      case "loading":
        return (
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
        );
      case "success":
        return (
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
            <svg
              className="h-10 w-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        );
      case "failed":
      case "error":
        return (
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
            <svg
              className="h-10 w-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "success":
        return "text-green-600";
      case "failed":
      case "error":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardBody className="p-6 sm:p-8">
          <div className="text-center space-y-6">
            {getStatusIcon()}
            
            <div>
              <h2 className={`text-2xl font-bold mb-2 ${getStatusColor()}`}>
                {status === "loading" && "Processing Payment"}
                {status === "success" && "Payment Successful"}
                {status === "failed" && "Payment Failed"}
                {status === "error" && "Error"}
              </h2>
              <p className="text-gray-600">{message}</p>
            </div>

            {status !== "loading" && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {searchParams.get("userId") ? ( 
                  <Button
                    variant="primary"
                    onClick={() => navigate(`${ROUTES.REGISTER}?userId=${searchParams.get("userId")}&step=4`)}
                  >
                    Continue
                  </Button>
                ) : ( 
                  <>
                    <Button
                      variant="primary"
                      onClick={() => navigate(ROUTES.DASHBOARD)}
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(ROUTES.HOME)}
                    >
                      Back to Home
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default PaymentResultPage;

